import { QueryKey } from '@tanstack/react-query';
import { queryClient } from 'queryClient';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import { getIntegrationAgencySettingsQueryKey } from 'hooks/useFetchIntegrationAgencySettings';
import { Integration } from 'models/InternalIntegrationSetting';
import { IntegrationMutationLevel } from 'pages/integrations/hooks/useDisableIntegrationMutation';
import useEnableIntegrationMutation from 'pages/integrations/hooks/useEnableIntegrationMutation';
import useRedirectToIntegrationDetails from 'pages/integrations/hooks/useRedirectToIntegrationDetails';
import { Button, CheckboxField } from '@hostfully/ui-components';
import { HF_ACCOUNTING_CONTACT_URL } from '../marketing/HostfullyAccounting.constants';
import ModalHeader from './components/ModalHeader';
import {
  BodyText,
  CheckboxGroupWrapper,
  CheckboxLabelWrapper,
  ConsentWrapper,
  EnrollmentFooter,
  ModalContainer,
} from './EnrollmentModal.styles';
import {
  CONSENT_CHECKBOX_TESTID,
  CONTACT_SALES_BUTTON_TESTID,
  CONTINUE_BUTTON_TESTID,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
} from './EnrollmentModal.constants';

interface EnrollmentModalProps {
  modalId: string;
  integration: Integration;
  queryKey: QueryKey;
}

const EnrollmentModal = ({
  modalId,
  integration,
  queryKey,
}: EnrollmentModalProps) => {
  const { t } = useTranslation();
  const { closeModal } = useAppModal();
  const { notifySuccess } = useNotify();
  const { enableIntegration, isLoading } = useEnableIntegrationMutation();
  const { redirectToIntegrationDetails } = useRedirectToIntegrationDetails();

  const methods = useForm<{ consent: boolean }>({
    defaultValues: { consent: false },
  });
  const hasConsented = methods.watch('consent');

  const onClose = () => closeModal(modalId);

  const onContactSales = () => {
    onClose();
    window.open(HF_ACCOUNTING_CONTACT_URL, '_blank', 'noopener,noreferrer');
  };

  const onEnroll = () => {
    if (isLoading) return;

    enableIntegration(
      {
        level: IntegrationMutationLevel.AGENCY,
        enumId: integration.enumId,
        queryKey,
        skipSuccessToast: true,
      },
      {
        onSuccess: () => {
          onClose();
          notifySuccess(
            <Trans
              i18nKey="pageHostfullyAccounting.enrollmentModal.welcomeToast"
              components={{ br: <br /> }}
            />,
          );
          const agencySettingsQueryKey = getIntegrationAgencySettingsQueryKey({
            enumId: integration.enumId,
          });
          // Remove (rather than invalidate) so the details page has no stale
          // cached status to redirect away on before the fresh fetch resolves.
          queryClient.removeQueries({ queryKey: agencySettingsQueryKey });

          // The BE takes ~5 minutes to provision the accessUrl after enrollment.
          // Invalidate once more so the navbar and the integrations list pick it
          // up without a manual refresh.
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: agencySettingsQueryKey });
            queryClient.invalidateQueries({ queryKey });
          }, 5 * 60 * 1000);
          redirectToIntegrationDetails(integration);
        },
      },
    );
  };

  return (
    <ModalContainer>
      <ModalHeader
        title={t('pageHostfullyAccounting.enrollmentModal.title')}
        closeLabel={t('common.close')}
        onClose={onClose}
      />

      <BodyText>
        <p>{t('pageHostfullyAccounting.enrollmentModal.body.requirement')}</p>
        <p>{t('pageHostfullyAccounting.enrollmentModal.body.question')}</p>
      </BodyText>

      <ConsentWrapper>
        <FormProvider {...methods}>
          <CheckboxGroupWrapper>
            <CheckboxField
              name="consent"
              data-testid={CONSENT_CHECKBOX_TESTID}
              label={
                <CheckboxLabelWrapper>
                  <Trans
                    i18nKey="pageHostfullyAccounting.enrollmentModal.checkboxLabel"
                    components={{
                      termsLink: (
                        // eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
                        <a
                          href={TERMS_OF_SERVICE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                      privacyLink: (
                        // eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
                        <a
                          href={PRIVACY_POLICY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  />
                </CheckboxLabelWrapper>
              }
            />
          </CheckboxGroupWrapper>
        </FormProvider>
      </ConsentWrapper>

      <EnrollmentFooter>
        <Button
          data-testid={CONTACT_SALES_BUTTON_TESTID}
          variant="primaryOutline"
          onClick={onContactSales}
        >
          {t('pageHostfullyAccounting.enrollmentModal.contactSalesBtn')}
        </Button>
        <Button
          data-testid={CONTINUE_BUTTON_TESTID}
          variant="primary"
          onClick={onEnroll}
          disabled={!hasConsented || isLoading}
        >
          {t('pageHostfullyAccounting.enrollmentModal.continueBtn')}
        </Button>
      </EnrollmentFooter>
    </ModalContainer>
  );
};

export default EnrollmentModal;
